import { formatBytes } from '@fieldia/core';

/**
 * A question as the survey editor draws it, and the settings in it: its card,
 * the bar beside it, the page cards, and each kind's own settings — the same
 * settings a field shows on the screen editor's canvas and panel.
 */
export const questions = {
  // ---- the survey editor
  /** What a text question's dotted line says. */
  preview: {
    'short-answer': 'Short answer text',
    paragraph: 'Long answer text',
    email: 'Email address',
    phone: 'Phone number',
    'one-time-code': 'A code of 6 digits',
    website: 'Web address',
    number: 'A number',
    amount: 'An amount',
    keywords: 'Keywords',
    date: 'Day, month, year',
    'date-time': 'Day, month, year, time',
    time: 'Hours and minutes',
  },
  addPage: 'Add page',
  formHeading: 'Form heading',
  formDescription: 'Form description',
  addQuestionAfter: 'Add a question after this one',
  addQuestion: 'Add question',
  addPageAfter: 'Add a page after this one',
  add: 'Add',
  whereAnswersLead: 'Where answers lead',
  findAdd: (kind: string) => `Add a question: ${kind}`,
  newQuestion: 'new question',
  goTo: (label: string) => `Go to “${label}”`,
  goToPage: (label: string) => `Go to the page “${label}”`,
  page: 'page',
  addAPage: 'Add a page',
  someAnswers: 'Shown only for some answers',
  dragToMove: 'Drag to move',
  dragTitle: 'Drag to move · Ctrl+Shift+K or J moves it too',
  question: 'Question',
  kindOfQuestion: 'Kind of question',
  kindNamed: (kind: string) => `Kind of question: ${kind}`,
  custom: 'Custom',
  description: 'Description',
  moreOptions: 'More options',
  showOnlyWhen: 'Show only when…',
  moveUp: 'Move up',
  moveDown: 'Move down',
  duplicate: 'Duplicate',
  delete: 'Delete',
  required: 'Required',
  pageTitle: 'Page title',
  untitledPage: 'Untitled page',
  deletePage: 'Delete page',
  deleteQuestion: 'Delete question',
  questionTools: 'Question tools',
  pageOf: (n: number, total: number) => `Page ${n} of ${total}`,
  // ---- each kind's settings
  from: 'From',
  to: 'to',
  toLabel: 'To',
  step: 'Step',
  severalAnswers: 'Several answers',
  pictures: 'Pictures',
  pictureAddress: 'Picture address, https://…',
  uploaded: 'Uploaded picture',
  pictureAlt: 'What it shows, for people who cannot see it',
  upload: 'Upload',
  pictureFor: (option: string) => `Picture for ${option}`,
  pictureShows: (option: string) => `What the picture for ${option} shows`,
  rows: 'Rows',
  columns: 'Columns',
  addRow: 'Add row',
  addColumn: 'Add column',
  row: (n: number) => `Row ${n}`,
  column: (n: number) => `Column ${n}`,
  removeRow: 'Remove row',
  removeColumn: 'Remove column',
  removeRowNamed: (label: string) => `Remove row ${label}`,
  removeColumnNamed: (label: string) => `Remove column ${label}`,
  addressParts: { street: 'Street', line2: 'Line 2', city: 'City', region: 'Region', postcode: 'Postcode', country: 'Country' },
  asksFor: 'Asks for',
  partsOfAddress: 'Parts of the address',
  fieldsInCard: 'Fields in each card',
  addField: 'Add field',
  atLeast: 'At least',
  atMost: 'At most',
  cardTitle: 'Card title',
  entry: 'Entry',
  buttonWords: 'Button words',
  addAnother: 'Add another',
  shuffle: 'Shuffle option order',
  givePoints: 'Give points: make it a quiz',
  points: 'Points',
  pointsFor: (option: string) => `Points for ${option}`,
  givePlaces: 'Limit places: how many may pick each',
  places: 'Places',
  placesFor: (option: string) => `Places for ${option}`,
  placesHint: 'Shown as places left, and Full, once your server counts who took them (the data source’s limits). A full one is refused when picked. Empty: no limit.',
  noLimit: 'No limit',
  giveLooks: 'Describe the options: a second line, an icon or a colour',
  looks: 'What each option shows',
  captionFor: (option: string) => `Second line of ${option}`,
  iconFor: (option: string) => `Icon of ${option}`,
  optionColourFor: (option: string) => `Colour of ${option}`,
  secondLine: 'Second line',
  noIcon: 'No icon',
  optionNoColourFor: (option: string) => `No colour for ${option}`,
  looksHint: 'A second line, an icon or a colour shows the choice in Fieldia’s own list, never the browser’s.',
  // ---- a quiz's question
  rightAnswers: 'Right answers',
  rightAnswerFor: (option: string) => `${option} is a right answer`,
  rightHint: 'Ticked: a right answer. With several ticked, a question of one answer takes any of them; one of several answers needs exactly those.',
  answersTaken: 'Answers it takes',
  answersTakenHint: 'Separated by commas: “Hyaluronidase, hyaluronidase enzyme”. Words whatever their case; a number as its value.',
  questionPoints: 'Points',
  questionPointsLabel: 'Points for a right answer to this question',
  feedbackRight: 'Said when right',
  feedbackWrong: 'Said when wrong',
  // ---- choices
  layOut: 'Lay out',
  oneColumn: 'One column',
  twoColumns: 'Two columns',
  threeColumns: 'Three columns',
  fourColumns: 'Four columns',
  automatic: 'Automatic',
  allInARow: 'All in a row',
  inARow: 'In a row',
  keptInPlace: 'Options kept in place',
  keepInPlace: 'Keep in place',
  searchTheList: 'Search the list',
  showLabels: 'Show labels',
  pictureSize: 'Picture size',
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  fit: 'Fit',
  fillTheCard: 'Fill the card',
  wholePicture: 'Whole picture',
  rankTop: 'Rank only the top',
  all: 'All',
  showAs: 'Show as',
  buttons: 'Buttons',
  switch: 'Switch',
  wordsForYes: 'Words for yes',
  wordsForNo: 'Words for no',
  yes: 'Yes',
  no: 'No',
  ownAnswers: 'Let people add their own',
  severalPerRow: 'Several answers per row',
  shuffleRows: 'Shuffle rows',
  onePerColumn: 'One answer per column',
  pickAStep: 'People can pick a step',
  colouredBadge: 'Coloured badge',
  linkPicture: 'Picture',
  linkLines: 'Lines under it',
  tagColours: 'Colours',
  opensRecord: 'Opens its record',
  colourFor: (label: string) => `Colour for ${label}`,
  // ---- the work tracker's pickers
  pathPicker: 'Path in a tree',
  checkTree: 'A tree to tick: a branch ticks all under it',
  treeKeeps: 'Keeps',
  treeKeepsAll: 'Every one ticked',
  treeKeepsTop: 'A whole branch as itself',
  treeKeepsLeaves: 'The last ones alone',
  treeSearchShown: 'A search over it',
  joinedBy: 'Names joined by',
  suggestionsShown: 'Suggestions',
  treeShown: 'The tree under them',
  addedFromButton: 'Added from a button',
  ownList: 'Fieldia’s own list',
  dotsInList: 'Dots in its list',
  noColourFor: (label: string) => `No colour of its own for ${label}`,
  // ---- text, numbers and dates
  mostCharacters: 'Most characters',
  /** A text typed in several languages, each by a person: its switch, its languages, one added or taken away, and those a required answer is typed in. */
  severalLanguages: 'Typed in several languages',
  typedIn: 'Typed in',
  addLanguage: 'Add a language',
  removeLanguage: (name: string) => `Take ${name} away`,
  requiredIn: 'Required in',
  any: 'Any',
  growsAsTyped: 'Grows as people type',
  aiTools: 'AI help',
  aiDraft: 'Draft',
  aiPolish: 'Polish',
  decimals: 'Decimals',
  usual: 'Usual',
  unitBefore: 'Unit before',
  unitAfter: 'Unit after',
  currencyShown: 'Currency as',
  currencyAs: { symbol: 'Its symbol, E£', code: 'Its code, EGP' },
  currencyGoes: 'Currency goes',
  currencyWhere: { usual: 'Where the language puts it', before: 'Before the amount', after: 'After the amount' },
  shortAmount: 'Short, as 4.5M',
  icon: 'Icon',
  stars: 'Stars',
  hearts: 'Hearts',
  thumbs: 'Thumbs up',
  numbers: 'Numbers',
  wordsAt: (end: 'start' | 'end') => `Words at the ${end}`,
  labelOptional: 'Label (optional)',
  start: 'Start',
  end: 'End',
  makeNps: 'Make it NPS',
  colourNps: 'Colour as NPS',
  earliest: 'Earliest',
  latest: 'Latest',
  anyDay: 'Any day',
  today: 'Today',
  daysAfter: 'Days after today',
  daysBefore: 'Days before today',
  aDay: 'A day',
  dayOf: (which: 'min' | 'max'): string => (which === 'min' ? 'Earliest day' : 'Latest day'),
  daysFrom: (before: boolean, which: 'min' | 'max') => `Days ${before ? 'before' : 'after'} today, ${which === 'min' ? 'earliest' : 'latest'}`,
  weekends: 'Weekends',
  allowed: 'Allowed',
  notSatSun: 'Not Saturday or Sunday',
  notFriSat: 'Not Friday or Saturday',
  someDays: 'Some days only',
  // ---- a short answer typed to a mask, and a one-time code's boxes
  mask: 'Mask',
  maskHelp: '9 a digit, a a letter, * either; the rest is written in: 9999 9999',
  inCapitals: 'Letters in capitals',
  // A short answer that keeps the spaces at its ends as it is left (`trim: false`): a template's "New: ".
  keepEnds: 'Keep the spaces at its ends',
  codeLength: 'Digits',
  codeLetters: 'Letters too',
  shorthand: 'Typed in shorthand: 3.5m, 500k',
  // ---- what a date keeps, and the Hijri calendar beside a day
  dateKeeps: 'Keeps',
  keepsDay: 'A day',
  keepsMonth: 'A month: Oct 2026',
  keepsYear: 'A year: 2026',
  hijriBeside: 'Hijri date beside it',
  startsToday: 'Starts on today',
  minutes: 'Minutes',
  anyMinute: 'Any minute',
  every: (n: string) => `Every ${n}`,
  earliestTime: 'Earliest time',
  latestTime: 'Latest time',
  suggestions: 'Suggestions',
  suggestionsLabel: 'Suggestions, one a line',
  separator: 'Separator',
  comma: 'Comma',
  semicolon: 'Semicolon',
  most: 'Most',
  colour: 'Colour',
  byProgress: 'By how far it has come',
  green: 'Always green',
  amber: 'Always amber',
  red: 'Always red',
  blue: 'Always blue',
  showsPercent: 'Shows the percent',
  // ---- structures
  pens: { '#1b2a5c': 'Blue-black', '#111111': 'Black', '#1d4ed8': 'Blue' } as Record<string, string>,
  ink: 'Ink',
  penWidth: 'Pen width',
  thin: 'Thin',
  thick: 'Thick',
  wordsOnPad: 'Words on the pad',
  signHere: 'Sign here',
  wordsUnder: 'Words under it',
  agree: 'I agree this is my signature',
  uploadSignature: 'People can upload a picture of it',
  signs: 'Signs',
  signsNothing: 'Nothing: a picture only',
  signsForm: 'Every answer on the page',
  signsChosen: 'The answers chosen',
  answersSigned: 'Answers it signs',
  signsHelp: 'Who signed, when and a fingerprint of the answers are kept with it, and the answers are locked once signed.',
  signerName: 'Signer’s name',
  signerTyped: 'The name typed, else the person signed in',
  mustBeFilled: 'Must be filled',
  partsFilled: 'Parts that must be filled',
  startsOn: 'Starts on',
  noCountry: 'No country',
  // ---- a phone read the world over: its own country and how it is shown, else the page's
  verifyCode: 'Confirmed by a code',
  verifyBy: 'Sent by',
  verifyByWhatsapp: 'WhatsApp',
  verifyBySms: 'SMS',
  phoneCountry: 'Country to start on',
  phoneShown: 'Shown',
  thePages: 'The page’s',
  phoneShownAs: { international: 'Abroad’s way: +20 10 1234 5678', national: 'At home’s way: 010 1234 5678' },
  phoneCountries: 'Countries picked from',
  phoneCountriesAs: { list: 'Fieldia’s list, found by typing', native: 'The browser’s own list' },
  addALine: 'Add a line',
  whenEmpty: 'When empty',
  noLines: 'No lines yet',
  askBeforeRemoving: 'Ask before removing a line',
  eachColumn: 'Each column',
  addUp: 'Add up',
  addUpNamed: (label: string) => `Add up ${label}`,
  shownNamed: (label: string) => `${label}: shown`,
  alwaysShown: 'Always shown',
  canHide: 'People can hide it',
  hiddenToStart: 'Hidden to start',
  canCreate: 'People can create new ones',
  onlyWhere: 'Only where',
  hasTheValue: 'has the value',
  ownFilter: 'Offers records by a filter of its own',
  toolbar: 'Formatting toolbar',
  // ---- the inline settings of the older kinds
  levels: 'Levels',
  currency: 'Currency',
  linksTo: 'Links to',
  fileTypes: { images: 'Images', pdf: 'PDF', documents: 'Documents', spreadsheets: 'Spreadsheets', presentations: 'Presentations', video: 'Video', audio: 'Audio' },
  largestFile: 'Largest file',
  anySize: 'Any size',
  /** A size of file, in the language's units. */
  size: (bytes: number) => formatBytes(bytes),
  severalFiles: 'More than one file',
  files: 'files',
  list: 'List',
  thumbnails: 'Thumbnails',
  cards: 'Cards',
  /** Whether the person filling the form may flip the files between a list and pictures. */
  peopleCanSwitch: 'People can switch',
  cameraOnPhones: 'Camera on phones',
  rearCamera: 'Rear camera',
  frontCamera: 'Front camera',
  takesOnly: 'Takes only',
  kindsOfFile: 'Kinds of file',
  showFilesAs: 'Show chosen files as',
  /** Photos made smaller before they are kept (`shrink`): the setting, the usual way, keeping them as taken, a side in pixels, and how sharp. */
  smallerPhotos: 'Make photos smaller',
  smallerUsual: 'As usual (2048 px)',
  smallerNo: 'No',
  smallerTo: (px: number) => `To ${px} px`,
  photoQuality: 'Photo quality',
  qualityUsual: 'As usual (85%)',
  qualityOf: (n: number) => `${n}%`,
  /** A photo set's own: its cover, captions, crop and turn, the original kept, the shape every crop keeps. */
  photoCover: 'The first photo is the cover',
  photoCaptions: 'A caption under each photo',
  photoEdit: 'Crop and turn',
  photoOriginal: 'Keep the original of a photo cropped',
  photoShape: 'Shape of a crop',
  photoShapeAny: 'Any shape',
  photosHelp: 'People put the photos in the order they want; the first is what a list’s card and a map’s pin show.',
  /** A file upload that records on the page (`options.record`): what it records, and the longest a recording may be (`maxSeconds`). */
  recordedOnPage: 'Recorded on the page',
  recordKinds: { '': 'Nothing', audio: 'A voice note', video: 'A video', both: 'A voice note or a video' } as Record<string, string>,
  longestRecording: 'Longest recording',
  recordUsual: 'As usual (2 min, a video 1 min)',
  recordFor: (seconds: number) => (seconds >= 60 && seconds % 60 === 0 ? (seconds === 60 ? '1 minute' : `${seconds / 60} minutes`) : `${seconds} seconds`),
  // ---- previews of the newer kinds
  addressLines: { street: 'Street address', line2: 'Address line 2', city: 'City', region: 'State or region', postcode: 'Postcode', country: 'Country' },
  entryOne: 'Entry 1',
  // ---- a table's columns
  columnKinds: { text: 'Text', number: 'Number', date: 'Date', 'yes-no': 'Yes or no', choice: 'Choice', other: 'Other' },
  kindOfColumn: (n: number) => `Kind of column ${n}`,
  choicesOfColumn: (label: string) => `Choices of ${label}`,
  /** A field of the record a line's choices may change with, beside the line's own columns. */
  ofTheRecord: (label: string) => `${label} (the record)`,
  columnListNote: 'The app gives each line its own choices, and again when what they change with changes on that line.',
  // ---- choices from the app's list
  choices: 'Choices',
  writtenHere: 'Written here',
  fromAppList: 'From the app’s list',
  listWord: 'List',
  listName: 'List name',
  noOtherFields: 'No other fields on the page yet.',
  changesWith: 'Changes with',
  listNote: 'The app gives these choices as the form opens, and again when a field they change with changes.',
  fromAnswers: 'Picked in another question',
  carriedFrom: 'Question',
  carriedWhich: 'Offer',
  carriedPicked: 'The answers picked there',
  carriedUnpicked: 'The answers not picked there',
  noOtherChoices: 'No other choice on the page yet.',
  carriedNote: 'Offered by their labels as they are picked there; this question’s own options follow them, such as “None of them”. One no longer picked there is taken out here, and said.',
  // ---- the business kinds' own
  noField: 'None',
  endsOn: 'Ends on',
  runsFrom: 'Runs from',
  keptIn: 'Kept in',
  inHours: 'Hours',
  inMinutes: 'Minutes',
  wordsBeside: 'Its words beside it',
  heightPx: 'Height (px)',
  /** A QR code's error correction, by how much of it may be lost and still read; its value's words under it. */
  qrLevel: 'Error correction',
  qrLevels: { L: 'Low (7%)', M: 'Medium (15%)', Q: 'Quartile (25%)', H: 'High (30%)' } as Record<string, string>,
  codeWordsUnder: 'Its words under it',
  /** Pins on a picture: its address, or the field holding it, and what it shows. */
  pinsPicture: 'Picture',
  pinsPictureFrom: 'Picture from',
  pinsShows: 'What it shows',
  accountsFrom: 'Accounts from',
  currencyFrom: 'Currency from',
  taxTyped: 'Tax amounts can be typed',
  amountDueFrom: 'Amount due from',
  propertyColumns: 'Columns',
  canAddProperty: 'People may add a property',
  // ---- a date and time's clock, booking slots
  timeZone: 'Time zone',
  pageZone: 'The page’s',
  schedule: 'Schedule',
  slotLength: 'Minutes a booking lasts',
  slotsBetween: 'Minutes kept free after one',
  bookingEndsIn: 'Its end in',
  askedAgainWhen: 'Asked again when',
  // ---- code
  codeLanguage: 'Language',
  plainText: 'Plain text',
};
